import {
  message,
  Card,
  Typography,
  Col,
  Badge,
  Tag,
  Button,
  Alert,
  Modal,
  DatePicker,
  Radio,
  Select,
} from "antd";
import GenericForm from "../../components/GenericForm";
import { AppContext } from "../../context/AppContext";
import { TourContext } from "../../context/TourContext";
import React, { useEffect, useContext, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { BorrowerService } from "../../utils/apiService";
import { LineWave } from "react-loader-spinner";
import "../../pages/Styles.css";
import {
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { uploadFile } from "../../utils/firebase";
import { calculateLoanPayments } from "../../utils/loanCalculations";
import CommoditySelector from "../../components/CommoditySelector";
import AIEligibilityService from "../../utils/aiEligibilityService";
import AIEligibilityDisplay from "../../components/AIEligibilityDisplay";
import GoogleSheetsService from "../../utils/googleSheetsService";
import { normalizeTermOptions } from "../../utils/loanUtils";

const AddLoan = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();
  const [loanTypes, setLoanTypes] = useState([]);
  const [borrowers, setBorrowers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [max, setMax] = useState(0);
  const { company } = useContext(AppContext);
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isHomeEssentials, setIsHomeEssentials] = useState(false);
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [selectedBorrower, setSelectedBorrower] = useState(
    location.state?.selectedBorrower || null
  );
  const [commodityTotal, setCommodityTotal] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);
  const [showCommodityWarning, setShowCommodityWarning] = useState(false);
  const { runTour, tourContent, endTour } = useContext(TourContext);
  const [aiEligibility, setAiEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [existingCollateral, setExistingCollateral] = useState([]);
  const [collateralOption, setCollateralOption] = useState("new"); // "new" or "existing"
  const [selectedCollateral, setSelectedCollateral] = useState(null);
  const aiService = new AIEligibilityService();
  const googleSheetsService = new GoogleSheetsService();

  // Track the placeholder text for amount
  const [amountPlaceholder, setAmountPlaceholder] =
    useState("Enter loan amount");

  const { Title, Text } = Typography;

  message.config({
    top: 80, // moves it down a bit from the top
    maxCount: 1, // how many messages can show at once
    duration: 3, // how many seconds the message stays
  });

  const handleLoanTypeChange = (loanTypeId) => {
    // Find the matching loan type from the state
    const selectedType = loanTypes.find((type) => type.value === loanTypeId);
    setSelectedLoanType(selectedType);

    if (selectedType) {
      // Update the placeholder with the max amount or any other property
      setAmountPlaceholder(
        `Enter loan amount [Max: ${selectedType.max_amount || "N/A"}]`
      );
      setMax(selectedType.max_amount);

      // Check if this is a Home Essentials loan
      const newIsHomeEssentials = selectedType.loan_name === "Home Essentials";

      // Only update state if it's actually changing
      if (newIsHomeEssentials !== isHomeEssentials) {
        setIsHomeEssentials(newIsHomeEssentials);

        // Reset selected commodities if switching from Home Essentials to another type
        if (!newIsHomeEssentials && selectedCommodities.length > 0) {
          setSelectedCommodities([]);
        }
      }
    } else {
      setAmountPlaceholder("Enter loan amount");

      // Only update these states if they're changing
      if (isHomeEssentials) {
        setIsHomeEssentials(false);
      }

      if (selectedCommodities.length > 0) {
        setSelectedCommodities([]);
      }
    }
  };

  const fetchBorrowers = async () => {
    try {
      const data = await BorrowerService.fetchBorrowers(token, company.id);
      const people = data.map((item) => ({
        label:
          item.first_name +
          " " +
          item.last_name +
          " ~ Score : " +
          item.credit_score,
        value: item.id,
        fullData: item, // Store full borrower data for AI analysis
      }));

      setBorrowers(people);
    } catch (error) {
      // Handle error state here
    }
  };

  const fetchLoanTypes = async () => {
    try {
      // Make the GET request using Axios
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loan_products?fields=*.*&filter[company]=${company.id}&filter[is_active][_eq]=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // Optional: Set content type if needed
          },
        }
      );

      const types = response.data.data.map((item) => ({
        label: item.loan_name, // What's displayed in the UI
        value: item.id, // The underlying value used in forms
        ...item,
      }));

      setLoanTypes(types);
    } catch (err) {
      // Handle errors and update the error state
      console.error("Error fetching loan types:", err);
      //  setError(err);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/branches?filter[company]=${company.id}&filter[status]=published`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const branchOptions = response.data.data.map((branch) => ({
        label: branch.branch_name,
        value: branch.id,
      }));

      setBranches(branchOptions);
    } catch (err) {
      console.error("Error fetching branches:", err);
      message.error("Failed to load branches. Please try again.");
    }
  };

  const fetchBorrowerCollateral = async (borrowerId) => {
    if (!borrowerId) {
      setExistingCollateral([]);
      return;
    }

    try {
      // Get loans for this borrower first, then get collateral for those loans
      const loansResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?filter[borrower]=${borrowerId}&filter[company]=${company.id}&fields=id,collateral`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const loans = loansResponse.data.data;
      const loanIds = loans.map(loan => loan.id);

      if (loanIds.length === 0) {
        setExistingCollateral([]);
        return;
      }

      // Get collateral for this borrower's loans that can be reused 
      // (exclude only Sold and Repossessed - allow Pending Verification, Active, Released)
      const collateralResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/collateral_register?filter[loan][_in]=${loanIds.join(',')}&filter[status][_nin]=Sold,Repossessed&filter[company]=${company.id}&fields=*`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const collateralOptions = collateralResponse.data.data.map((item) => ({
        label: `${item.description} - ZMW ${item.valuation_amount?.toLocaleString()} (${item.status})`,
        value: item.id,
        collateralData: item,
      }));

      setExistingCollateral(collateralOptions);
    } catch (err) {
      console.error("Error fetching borrower collateral:", err);
      setExistingCollateral([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all data in parallel for better performance
        await Promise.all([
          fetchBorrowers(),
          fetchLoanTypes(),
          fetchBranches(),
        ]);
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        // Once all calls are done (successfully or with error),
        // set loading to false
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch collateral for pre-selected borrower
  useEffect(() => {
    if (selectedBorrower?.id) {
      fetchBorrowerCollateral(selectedBorrower.id);
    }
  }, [selectedBorrower]);

  // Set form initial values when there's a selected borrower
  const getInitialValues = () => {
    if (selectedBorrower) {
      return {
        borrower: selectedBorrower.id,
      };
    }
    return {};
  };

  // AI Eligibility Check
  const checkAIEligibility = async (borrowerId, loanTypeId, amount) => {
    if (!borrowerId || !loanTypeId || !amount) return;

    setEligibilityLoading(true);
    try {
      // Get full borrower data
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/users/${borrowerId}?fields=*.*,loans.*.*`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const borrowerData = response.data.data;
      const loans = borrowerData.loans || [];

      // Find selected loan type
      const loanType = loanTypes.find((type) => type.value === loanTypeId);

      const loanRequest = {
        amount: amount,
        loan_type: loanType?.loan_name,
        purpose: loanType?.description,
      };

      // Get AI assessment
      const assessment = await aiService.assessEligibility(
        borrowerData,
        loans,
        loanRequest
      );
      setAiEligibility(assessment);

      // Show success message with recommendation
      const recommendationColor =
        {
          APPROVE: "success",
          CONDITIONAL_APPROVE: "warning",
          DECLINE: "error",
        }[assessment.recommendation] || "info";

      message[recommendationColor](
        `AI Assessment: ${assessment.recommendation} (${assessment.confidence}% confidence)`
      );
    } catch (error) {
      console.error("AI Eligibility Check Error:", error);
      message.error("Failed to perform AI eligibility check");
    } finally {
      setEligibilityLoading(false);
    }
  };

  // Update loan amount and check commodities total when amount changes
  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    setLoanAmount(amount);

    // Check if commodity total exceeds loan amount
    if (amount > 0 && commodityTotal > amount) {
      setShowCommodityWarning(true);
    } else {
      setShowCommodityWarning(false);
    }
  };

  const handleFinish = async (values) => {
    console.log("Form submission started with values:", values);

    // Set application_date to today if not provided
    if (!values.application_date) {
      values.application_date = new Date().toISOString().split("T")[0];
    } else {
      // Convert DatePicker value to string format
      values.application_date = values.application_date.format("YYYY-MM-DD");
    }

    // Check if Home Essentials loan has commodities selected
    if (isHomeEssentials && selectedCommodities.length === 0) {
      message.error(
        "Please select at least one commodity for Home Essentials loan."
      );
      return;
    }

    // Check if commodity total exceeds loan amount before submission
    if (isHomeEssentials && commodityTotal > values.amount) {
      message.error(
        "Total commodity cost cannot exceed loan amount. Please adjust your selections."
      );
      return;
    }

    try {
      setUploading(true);

      const termOptions = normalizeTermOptions(
        selectedLoanType?.loan_term_options
      );
      const resolvedLoanTerm =
        termOptions.length > 0
          ? values.loan_term || termOptions[0]
          : selectedLoanType?.loan_term;
      const loanProductForCalc =
        termOptions.length > 0
          ? { ...selectedLoanType, loan_term: resolvedLoanTerm }
          : selectedLoanType;

      const paymentDetails = calculateLoanPayments(
        loanProductForCalc,
        values.amount
      );

      // Use the pre-selected borrower if available
      const finalBorrower = selectedBorrower
        ? selectedBorrower.id
        : values.borrower;

      // Calculate expected return amount using FV formula: FV(rate, nper, pmt, pv)
      // FV = principal * (1 + rate)^nper
      const effectiveInterestRate = values.custom_interest_rate
        ? values.custom_interest_rate / 100
        : selectedLoanType.interest_rate / 100;

      const expectedReturnAmount = values.amount * (1 + effectiveInterestRate);
      const profitAmount = expectedReturnAmount - values.amount;

      // 1. Create the loan first
      const loanPayload = {
        ...values,
        borrower: finalBorrower, // Ensure we use the correct borrower ID
        company: company.id,
        loan_status: 1,
        status: "published",
        branch: values.branch, // Add branch ID to the payload
        custom_interest_rate: values.custom_interest_rate || null, // Store custom rate if provided
        interest_applied: values.custom_interest_rate || selectedLoanType.interest_rate, // Store the actual rate used
        next_payment_date: paymentDetails.next_payment_date,
        next_payment_amount: paymentDetails.next_payment_amount,
        total_amount_due: paymentDetails.total_amount_due,
        interest_amount: paymentDetails.interest_amount,
        expected_return_amount: expectedReturnAmount, // FV calculation
        profit_amount: profitAmount, // Interest/profit
        ...(selectedLoanType?.auto_split_payments &&
          paymentDetails.payment_schedule && {
            payment_schedule: paymentDetails.payment_schedule,
          }),
        ...(termOptions.length > 0 && { loan_term: resolvedLoanTerm }),
        // If this is a Home Essentials loan, include commodity information
        ...(isHomeEssentials && {
          is_commodity_loan: true,
          commodity_list: selectedCommodities.map((item) => item.id),
        }),
      };

      const loanResponse = await axios.post(
        "https://zedlenders.pickmesms.com/items/loans",
        loanPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const loanId = loanResponse.data.data.id;
      const createdLoan = loanResponse.data.data;

      // Auto-create amortization schedule if auto_split_payments is enabled
      if (selectedLoanType?.auto_split_payments && paymentDetails.payment_schedule) {
        try {
          const borrowerData = borrowers.find(
            (b) => b.value === finalBorrower
          )?.fullData;
          const borrowerNrc = borrowerData?.nrc || "";

          const effectiveRate = values.custom_interest_rate
            || selectedLoanType.interest_rate;

          const amortizationEntries = paymentDetails.payment_schedule.map((entry) => {
            const dueDate = new Date(entry.due_date);
            const monthName = dueDate.toLocaleString("en-US", {
              month: "long",
              year: "numeric",
            }).toUpperCase();

            const amountDue = entry.principal || entry.amount;
            const interestDecimal = effectiveRate / 100;
            const expectedAmount = Math.round(amountDue * (1 + interestDecimal) * 100) / 100;
            const profit = Math.round(amountDue * interestDecimal * 100) / 100;

            return {
              loan_reference: loanId,
              borrower_nrc: borrowerNrc,
              month: monthName,
              due_date: entry.due_date.split("T")[0],
              amount_due: amountDue,
              interest_rate: effectiveRate,
              expected_amount: expectedAmount,
              profit: profit,
              status: "pending",
              sheet: monthName,
            };
          });

          await axios.post(
            "https://zedlenders.pickmesms.com/items/amortization",
            amortizationEntries,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (amortError) {
          console.error("Failed to create amortization schedule:", amortError);
          message.warning(
            "Loan created but amortization schedule failed to generate. You can add it manually from the loan details page.",
            10
          );
        }
      }

      // If this is a commodity loan, save details to loan_commodity_details
      if (isHomeEssentials && selectedCommodities.length > 0) {
        try {
          // Create records in loan_commodity_details for each commodity
          const commodityDetailsPromises = selectedCommodities.map((item) => {
            const detailsPayload = {
              loan_id: loanId,
              commodity_id: item.id,
              quantity: item.quantity,
              selling_price: item.selling_price, // Use the actual selling_price field
              status: "published",
            };

            return axios.post(
              "https://zedlenders.pickmesms.com/items/loan_commodity_details",
              detailsPayload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          });

          await Promise.all(commodityDetailsPromises);
        } catch (commodityDetailsError) {
          console.error(
            "Failed to save commodity details:",
            commodityDetailsError
          );
          message.warning(
            "Loan created but failed to save commodity details. Please contact support.",
            10
          );
        }
      }

      // 2. Handle collateral uploads if required
      if (selectedLoanType?.requires_collateral) {
        if (collateralOption === "existing" && selectedCollateral) {
          // Link existing collateral to this loan
          try {
            await axios.patch(
              `https://zedlenders.pickmesms.com/items/loans/${loanId}`,
              {
                collateral: selectedCollateral.id,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } catch (existingCollateralError) {
            console.error("Failed to link existing collateral:", existingCollateralError);
            message.warning(
              "Loan created but failed to link existing collateral. Please contact support.",
              10
            );
          }
        } else if (collateralOption === "new" && values.collateralImages) {
          try {
          // Handle both array format and object with fileList
          const imageFileList = Array.isArray(values.collateralImages)
            ? values.collateralImages
            : values.collateralImages.fileList || [];

          const docFileList = Array.isArray(values.inspectionDocument)
            ? values.inspectionDocument
            : values.inspectionDocument.fileList || [];

          console.log(
            "Processing uploads - Images:",
            imageFileList.length,
            "Documents:",
            docFileList.length
          );

          const imageUrls = imageFileList.map(async (file) => {
            return uploadFile(file.originFileObj, loanId);
          });

          // Upload document
          const urls = await Promise.all(imageUrls);

          const docUrl = await uploadFile(
            docFileList[0].originFileObj,
            loanId,
            "document"
          );

          // 3. Create collateral record in Directus
          const collateralPayload = {
            loan: loanId,
            documents: urls,
            inspection_document: docUrl,
            description: values.collateralDescription || "",
            valuation_amount: values.collateralValue || 0,
            status: "Pending Verification",
            company: company.id,
            registration_date: new Date().toISOString().split("T")[0], // Current date
          };

          console.log(collateralPayload);

          const collateralResponse = await axios.post(
            "https://zedlenders.pickmesms.com/items/collateral_register",
            collateralPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // Update loan with collateral ID
          await axios.patch(
            `https://zedlenders.pickmesms.com/items/loans/${loanId}`,
            {
              data: {
                // Wrap the update in a "data" object
                collateral: collateralResponse.data.data.id,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (collateralError) {
          console.error("Collateral submission failed:", collateralError);
          message.warning(
            "Loan created but collateral documents failed to upload. Please contact support.",
            10
          );
          return;
          }
        }
      }

      // Update Google Sheets with new loan data
      try {
        console.log("🔵 Starting Google Sheets update...");
        console.log("🔵 Created loan data:", createdLoan);

        // Get additional data for Google Sheets
        const borrowerData = borrowers.find(
          (b) => b.value === finalBorrower
        )?.fullData;
        const loanTypeData = loanTypes.find(
          (t) => t.value === values.loan_product
        );
        const branchData = branches.find((b) => b.value === values.branch);

        const googleSheetsData = {
          ...createdLoan,
          borrower: borrowerData,
          loan_product: loanTypeData,
          branch: branchData,
          application_date: new Date().toISOString().split("T")[0],
          commodity_list: isHomeEssentials
            ? selectedCommodities.map((item) => item.id)
            : [],
          is_commodity_loan: isHomeEssentials,
        };

        console.log("🔵 Google Sheets data prepared:", googleSheetsData);

        await googleSheetsService.addLoanToSheet(googleSheetsData);
        console.log("✅ Loan added to Google Sheets successfully");
        message.success("Loan created and added to Google Sheets tracking!", 3);
      } catch (googleSheetsError) {
        console.error("❌ Failed to update Google Sheets:", googleSheetsError);
        // Don't fail the whole process if Google Sheets update fails
        message.warning(
          "Loan created successfully, but failed to update Google Sheets tracking.",
          5
        );
      }

      message.success("Loan and collateral documents submitted successfully!");

      // Redirect to appropriate page after 2 seconds
      setTimeout(() => {
        if (selectedBorrower) {
          // If we came from a borrower profile, go back there
          navigate(`/borrower/${selectedBorrower.id}`);
        } else {
          // Otherwise go to the loans list
          navigate("/loans/1");
        }
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      message.error(
        error.response?.data?.errors?.[0]?.message ||
          "Failed to submit loan application"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFinishFailed = (errorInfo) => {
    console.log("Form validation failed:", errorInfo);

    // Find specific field errors
    const fieldErrors = errorInfo.errorFields || [];
    const errorMessages = fieldErrors.map((field) => {
      const fieldName = Array.isArray(field.name)
        ? field.name.join(".")
        : field.name;
      const firstError = field.errors && field.errors[0];
      return `${fieldName}: ${firstError}`;
    });

    if (errorMessages.length > 0) {
      message.error(`Form validation errors: ${errorMessages.join("; ")}`);
    } else {
      message.error("Please check the form for errors!");
    }
  };

  const collateralOptionFields = selectedLoanType?.requires_collateral
    ? [
        {
          label: "Collateral Option",
          name: "collateralOption",
          type: "custom",
          rules: [{ required: true, message: "Please select a collateral option" }],
          component: (
            <Radio.Group
              value={collateralOption}
              onChange={(e) => {
                setCollateralOption(e.target.value);
                setSelectedCollateral(null);
              }}
            >
              <Radio value="new">Create New Collateral</Radio>
              <Radio value="existing" disabled={existingCollateral.length === 0}>
                Use Existing Collateral
                {existingCollateral.length === 0 && (
                  <span style={{ color: '#999', fontSize: '12px' }}>
                    {' '}(No existing collateral available)
                  </span>
                )}
              </Radio>
            </Radio.Group>
          ),
        },
        ...(collateralOption === "existing" && existingCollateral.length > 0
          ? [
              {
                label: "Select Existing Collateral",
                name: "existingCollateral",
                type: "select",
                placeholder: "Choose existing collateral",
                options: existingCollateral,
                rules: [{ required: true, message: "Please select existing collateral" }],
                props: {
                  showSearch: true,
                  optionFilterProp: "children",
                  onChange: (collateralId) => {
                    const selected = existingCollateral.find(c => c.value === collateralId);
                    setSelectedCollateral(selected?.collateralData || null);
                  },
                },
              },
            ]
          : []),
      ]
    : [];

  const collateralFields = selectedLoanType?.requires_collateral && collateralOption === "new"
    ? [
        {
          label: "Collateral Description",
          name: "collateralDescription",
          type: "textarea",
          placeholder: "Describe the collateral",
          rules: [
            { required: true, message: "Please describe the collateral" },
          ],
        },
        {
          label: "Estimated Value (ZMW)",
          name: "collateralValue",
          type: "number",
          placeholder: "Enter collateral value",
          rules: [{ required: true, message: "Please enter estimated value" }],
          props: {}, // Add empty props object as a fallback
        },
        {
          label: "Collateral Images",
          name: "collateralImages",
          type: "upload",
          rules: [
            {
              required: false,
              validator: (_, value) => {
                console.log("Collateral images validation:", value);
                // Handle both array format and object with fileList
                const fileList = Array.isArray(value)
                  ? value
                  : value?.fileList || [];

                if (!fileList || fileList.length === 0) {
                  //return Promise.reject("Please upload collateral images");
                }
                if (fileList.length < 2) {
                  //return Promise.reject("At least 2 images required");
                }
                if (fileList.length > 10) {
                  //return Promise.reject("Maximum 10 images allowed");
                }
                return Promise.resolve();
              },
            },
          ],
          props: {
            multiple: true,
            accept: "image/*,.pdf,.doc,.docx",
            listType: "picture-card", // Must match this exact string
            maxCount: 10,
            // Add preview functionality
            onPreview: (file) => {
              const imageUrl =
                file.url || URL.createObjectURL(file.originFileObj);
              window.open(imageUrl, "_blank");
            },
          },
        },
        {
          label:
            "Inspection Document (with attached white book, title deed, etc.)",
          name: "inspectionDocument",
          type: "upload",
          rules: [
            {
              required: false,
              validator: (_, value) => {
                console.log("Inspection document validation:", value);
                // Handle both array format and object with fileList
                const fileList = Array.isArray(value)
                  ? value
                  : value?.fileList || [];

                if (!fileList || fileList.length === 0) {
                  //return Promise.reject("Please upload inspection document");
                }
                if (fileList.length > 1) {
                  return Promise.reject("Only one document allowed");
                }
                return Promise.resolve();
              },
            },
          ],
          props: {
            accept: ".pdf,.doc,.docx,.xlsx,.txt",
            maxCount: 1,
            listType: "text",
          },
        },
      ]
    : [];

  // Home Essentials commodity selection field
  const commodityField = isHomeEssentials
    ? [
        {
          label: (
            <span>
              <ShoppingCartOutlined /> Select Commodities
            </span>
          ),
          name: "commodities",
          type: "custom",
          rules: [
            {
              validator: (_, value) => {
                // Check if commodities are selected based on the state
                if (
                  isHomeEssentials &&
                  (!selectedCommodities || selectedCommodities.length === 0)
                ) {
                  return Promise.reject("Please select at least one commodity");
                }
                return Promise.resolve();
              },
            },
          ],
          component: (
            <>
              <CommoditySelector
                onChange={(commodities) => {
                  // Only update if value actually changed to prevent infinite loops
                  if (
                    JSON.stringify(commodities) !==
                    JSON.stringify(selectedCommodities)
                  ) {
                    setSelectedCommodities(commodities);
                  }
                }}
                value={selectedCommodities}
              />
              {showCommodityWarning && (
                <Alert
                  message="Warning"
                  description="Total commodity cost exceeds loan amount. Please adjust your selections or increase the loan amount."
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginTop: 16 }}
                />
              )}
              <div style={{ marginTop: 8, textAlign: "right" }}>
                <Text type="secondary">
                  Total Commodity Cost:{" "}
                  <Text strong>
                    {commodityTotal.toLocaleString("en-ZA")} ZMW
                  </Text>
                </Text>
              </div>
            </>
          ),
        },
      ]
    : [];

  const loanTermOptions = normalizeTermOptions(
    selectedLoanType?.loan_term_options
  );
  const loanTermField =
    loanTermOptions.length > 0
      ? [
          {
            label: `Tenure (${selectedLoanType?.term_unit || "months"})`,
            name: "loan_term",
            type: "select",
            placeholder: "Select tenure",
            options: loanTermOptions.map((term) => ({
              label: `${term} ${selectedLoanType?.term_unit || ""}`.trim(),
              value: term,
            })),
            rules: [{ required: true, message: "Please select loan tenure!" }],
            props: {
              showSearch: false,
              className: "loan-tenure-selector",
            },
          },
        ]
      : [];

  const fields = [
    {
      label: "Borrower",
      name: "borrower",
      type: "select",
      placeholder: "Select Borrower",
      options: borrowers,
      rules: [{ required: true, message: "Please select borrower!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
        disabled: !!selectedBorrower, // Disable if borrower is pre-selected
        className: "borrower-selector",
        onChange: (borrowerId) => {
          // Clear AI eligibility when borrower changes
          setAiEligibility(null);
          // Fetch collateral for the selected borrower
          fetchBorrowerCollateral(borrowerId);
        },
      },
    },

    {
      label: "Loan Type",
      name: "loan_product",
      type: "select",
      placeholder: "Select Loan Type",
      options: loanTypes,
      rules: [{ required: true, message: "Please select loan type!" }],
      props: {
        // Optional: Customize search behavior for this select
        showSearch: true, // Already true by default, included here for clarity
        optionFilterProp: "children", // Default, included here for clarity
        onChange: (loanTypeId) => {
          handleLoanTypeChange(loanTypeId);
          // Clear AI eligibility when loan type changes
          setAiEligibility(null);
        },
        className: "loan-type-selector",
        // filterOption: (input, option) =>
        //   option.children.toLowerCase().includes(input.toLowerCase()),
      },
    },
    ...loanTermField,
    {
      label: "Branch",
      name: "branch",
      type: "select",
      placeholder: "Select Branch",
      options: branches,
      rules: [{ required: true, message: "Please select a branch!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
      },
    },
    {
      label: "Amount (ZMW)",
      name: "amount",
      type: "number",
      placeholder: amountPlaceholder,
      rules: [
        {
          required: true,
          message: "Please input loan amount!",
        },
      ],
      props: {
        max: max,
        onChange: (value) => {
          handleAmountChange(value);
          // Clear AI eligibility when amount changes
          setAiEligibility(null);
        },
        className: "loan-amount-field",
      },
    },
    // Custom interest rate field (only shown if loan product allows it)
    ...(selectedLoanType?.allow_custom_interest_rate
      ? [
          {
            label: `Custom Interest Rate (%) - Range: ${selectedLoanType.interest_rate_min}% to ${selectedLoanType.interest_rate_max}%`,
            name: "custom_interest_rate",
            type: "number",
            placeholder: `Default: ${selectedLoanType.interest_rate}%`,
            rules: [
              {
                required: false,
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (
                    value < selectedLoanType.interest_rate_min ||
                    value > selectedLoanType.interest_rate_max
                  ) {
                    return Promise.reject(
                      new Error(
                        `Rate must be between ${selectedLoanType.interest_rate_min}% and ${selectedLoanType.interest_rate_max}%`
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ],
            props: {
              min: selectedLoanType.interest_rate_min,
              max: selectedLoanType.interest_rate_max,
              step: 0.01,
              precision: 2,
              formatter: (value) => (value ? `${value}%` : ""),
              parser: (value) => value.replace("%", ""),
            },
          },
        ]
      : []),
    {
      label: "Application Date",
      name: "application_date",
      type: "date",
      placeholder: "Select application date (defaults to today)",
      rules: [],
      props: {
        format: "YYYY-MM-DD",
        placeholder: "Leave blank for today's date",
        style: { width: "100%" },
      },
    },
    // AI Eligibility Check Field
    // {
    //   label: "AI Eligibility Assessment",
    //   name: "aiCheck",
    //   type: "custom",
    //   component: (
    //     <div>
    //       <Button
    //         type="primary"
    //         ghost
    //         loading={eligibilityLoading}
    //         onClick={() => {
    //           // Get current form values
    //           const formData = new FormData(document.querySelector('form'));
    //           const borrowerId = selectedBorrower?.id || formData.get('borrower');
    //           const loanTypeId = formData.get('loan_product');
    //           const amount = formData.get('amount');

    //           if (borrowerId && loanTypeId && amount) {
    //             checkAIEligibility(borrowerId, loanTypeId, parseFloat(amount));
    //           } else {
    //             message.warning('Please fill in Borrower, Loan Type, and Amount first');
    //           }
    //         }}
    //         style={{ marginBottom: 16 }}
    //         icon={<BulbOutlined />}
    //       >
    //         {eligibilityLoading ? 'Analyzing...' : 'Check AI Eligibility'}
    //       </Button>

    //       <AIEligibilityDisplay
    //         eligibility={aiEligibility}
    //         onRecheck={() => {
    //           const formData = new FormData(document.querySelector('form'));
    //           const borrowerId = selectedBorrower?.id || formData.get('borrower');
    //           const loanTypeId = formData.get('loan_product');
    //           const amount = formData.get('amount');

    //           if (borrowerId && loanTypeId && amount) {
    //             checkAIEligibility(borrowerId, loanTypeId, parseFloat(amount));
    //           }
    //         }}
    //         loading={eligibilityLoading}
    //       />
    //     </div>
    //   ),
    // },
    ...commodityField,
    ...collateralOptionFields,
    ...collateralFields,
  ];
  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}
      >
        <LineWave
          visible={true}
          height="100"
          width="100"
          color="#36454F"
          ariaLabel="line-wave-loading"
          wrapperStyle={{}}
          wrapperClass=""
          firstLineColor=""
          middleLineColor=""
          lastLineColor=""
        />
      </div>
    );
  }

  return (
    <Col xs={24} sm={16} md={16} lg={12} xl={12}>
      {/* Tour Modal */}
      {runTour && tourContent && (
        <Modal
          title={tourContent.title || "Loan Application Guide"}
          open={runTour}
          onCancel={() => endTour(false)}
          footer={[
            <Button key="skip" onClick={() => endTour(false)}>
              Skip
            </Button>,
            <Button key="next" type="primary" onClick={() => endTour(true)}>
              Got it!
            </Button>,
          ]}
          width={500}
        >
          <div style={{ padding: "10px 0" }}>
            <Alert
              message={
                tourContent.message || "Creating a new loan application!"
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text>Steps to create a loan:</Text>
            <ul>
              {tourContent.details?.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      <Card>
        {selectedBorrower && (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/borrower/${selectedBorrower.id}`)}
              style={{ marginBottom: 8 }}
            >
              Back to Borrower Profile
            </Button>
          </div>
        )}

        <Title
          level={3}
          className="loan-application-title"
          style={{ textAlign: "center", marginBottom: 24 }}
        >
          New Loan
        </Title>

        {selectedBorrower && (
          <div style={{ marginBottom: 16, textAlign: "center" }}>
            <Tag color="blue" style={{ padding: "5px 10px", fontSize: "14px" }}>
              Creating loan for: {selectedBorrower.name}
            </Tag>
            {selectedBorrower.nrc && (
              <Tag
                color="green"
                style={{ padding: "5px 10px", fontSize: "14px", marginLeft: 8 }}
              >
                NRC: {selectedBorrower.nrc}
              </Tag>
            )}
          </div>
        )}

        <GenericForm
          fields={fields}
          onFinish={handleFinish}
          onFinishFailed={handleFinishFailed}
          layout="vertical"
          initialValues={getInitialValues()}
          submitButtonText="Submit Loan Application"
          submitButtonProps={{
            loading: uploading,
            disabled: uploading,
            loadingText: "Securing Collateral...",
            style: { width: "100%" },
          }}
        />
      </Card>
    </Col>
  );
};

export default AddLoan;
